import axios from 'axios';
import { getAccessToken, getRefreshToken, refreshAccessToken, clearTokens } from '../utils/authTokens';
import eventBus from '../utils/eventBus';

// Small ring buffer of the most recent failed requests — the "Sent
// automatically" card in the in-view help support form (step 9) reads the
// last one as diagnostic context. Kept here (not per-component state) since
// the failure that prompted a support message is usually on a page the user
// has already navigated away from by the time they open the form.
const MAX_TRACKED_FAILURES = 5;
const recentFailures = [];

const trackFailure = (error) => {
  const config = error.config || {};
  recentFailures.push({
    method: (config.method || '').toUpperCase(),
    url: config.url,
    status: error.response?.status ?? null,
    timestamp: Date.now(),
  });
  if (recentFailures.length > MAX_TRACKED_FAILURES) recentFailures.shift();
};

export const getLastFailedRequest = () => recentFailures[recentFailures.length - 1] || null;

/**
 * Registers a global request interceptor (attaches the Bearer access token)
 * and a global response interceptor (refreshes once on 401 and retries) on
 * the default axios instance used by every api/*Client.js and component.
 *
 * `getBackendUrl` is a function so the backend URL can be resolved lazily,
 * after app-config.json has loaded.
 */
export const installAxiosAuth = (getBackendUrl) => {
  axios.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      trackFailure(error);
      const originalRequest = error.config;
      const isRefreshCall = originalRequest?.url?.includes('/auth/refresh');

      // A plan limit / missing feature is a normal outcome, not a bug: surface
      // it once through the global EntitlementSnackbar and let the caller's
      // own error path continue.
      if (error.response?.status === 402 && error.response?.data?.code === 'ENTITLEMENT_DENIED') {
        eventBus.emit(eventBus.EVENTS.ENTITLEMENT_DENIED, error.response.data);
      }

      if (error.response?.status === 401 && originalRequest && !isRefreshCall && !originalRequest._retry) {
        // No refresh token means no session to recover: reject and let the
        // router/caller handle it. Hard-redirecting here would reload a public
        // page (e.g. /login) that fires a protected call on mount, looping.
        if (!getRefreshToken()) {
          return Promise.reject(error);
        }
        originalRequest._retry = true;
        try {
          const backendUrl = getBackendUrl();
          const newAccessToken = await refreshAccessToken(backendUrl);
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          clearTokens();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};
